import React, { useState, useEffect } from 'react';
import CenteredModal from '../CenteredModal';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Tooltip from '../Tooltip';
import adminDocumentationApi from '~/api/adminDocumentationApi';
import './ScreenDocumentation.scss';

const ScreenDocumentation = ({ docKey, className = "", children }) => {
    const [showDoc, setShowDoc] = useState(false);
    const [lang, setLang] = useState('en'); 
    const [docData, setDocData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (showDoc && !docData && !error) {
            const fetchDoc = async () => {
                setLoading(true);
                try {
                    const res = await adminDocumentationApi.getByCode(docKey);
                    console.log("Response from getByCode:", res);
                    if (res && res.data && res.data.DocCode) {
                        setDocData(res.data);
                    } else if (res && res.data && res.data.result) { // Fallback if API hasn't updated or returns .result
                        setDocData(res.data.result);
                    } else if (res && res.DocCode) { // Fallback if already unpacked
                        setDocData(res);
                    } else {
                        setError(`Doc missing: ${docKey}`);
                    }
                } catch (err) {
                    setError("Failed to load documentation.");
                } finally {
                    setLoading(false);
                }
            };
            fetchDoc();
        }
    }, [showDoc, docData, docKey, error]);

    const currentTitle = docData ? (lang === 'vi' ? docData.Title_VI : docData.Title_EN) || docData.Title_EN : '';
    const currentContent = docData ? (lang === 'vi' ? docData.Content_VI : docData.Content_EN) || docData.Content_EN : '';

    const triggerContent = children ? children : (
        <Tooltip text="View Documentation" placement="top">
            <span 
                role="button"
                tabIndex={0}
                className={`btn btn-link p-0 text-decoration-none text-primary d-flex align-items-center ${className}`}
                onClick={(e) => {
                    e.stopPropagation();
                    setShowDoc(true);
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.stopPropagation();
                        e.preventDefault();
                        setShowDoc(true);
                    }
                }}
                style={{ outline: 'none', boxShadow: 'none' }}
            >
                <i className="bi bi-info-circle-fill" style={{ fontSize: '1.1em', opacity: 0.85 }}></i>
            </span>
        </Tooltip>
    );

    return (
        <React.Fragment>
            <div className="d-flex align-items-center">
                {children ? (
                    <span 
                        role="button"
                        tabIndex={0}
                        className={className}
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowDoc(true);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.stopPropagation();
                                e.preventDefault();
                                setShowDoc(true);
                            }
                        }}
                        style={{ outline: 'none', boxShadow: 'none' }}
                    >
                        {children}
                    </span>
                ) : (
                    triggerContent
                )}
            </div>

            <CenteredModal 
                show={showDoc} 
                onClose={() => setShowDoc(false)}
                title={loading ? "Loading..." : (error ? "Error" : currentTitle)}
                isDrawer={true}
                drawerWidth="600px"
                closeText="Close"
            >
                {!loading && !error && (
                    <div className="d-flex justify-content-end align-items-center mb-3 px-2 pt-2 border-bottom pb-2">
                        <span className="me-2 fs-9 text-muted fw-bold">Language:</span>
                        <div className="btn-group btn-group-sm" role="group">
                            <button 
                                type="button" 
                                className={`btn ${lang === 'en' ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => setLang('en')}
                            >
                                English
                            </button>
                            <button 
                                type="button" 
                                className={`btn ${lang === 'vi' ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => setLang('vi')}
                            >
                                Vietnamese
                            </button>
                        </div>
                    </div>
                )}
                
                <div className="markdown-body p-2 pt-0">
                    {loading ? (
                        <div className="d-flex justify-content-center p-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : error ? (
                        <div className="alert alert-danger m-3">{error}</div>
                    ) : (
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {currentContent}
                        </ReactMarkdown>
                    )}
                </div>
            </CenteredModal>
        </React.Fragment>
    );
};

export default ScreenDocumentation;
